import classNames from "classnames";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import MultiSelect from "components/common/MultiSelect";
import { nationalityData } from "features/practitioner/utils";
import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { GetIndividualBranch } from "../config/branchApi";
import { useParams } from "react-router-dom";
import { branchList, cityList } from "../utils";
import FalconComponentCard from "components/common/FalconComponentCard";
const isEnglish = (value) => /^[A-Za-z0-9\s'-]+$/.test(value);

const isArabic = (value) => /^[\u0600-\u06FF\s]+$/.test(value);
const AddBranchForm = ({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  reset,
  view,
  isSubmitted,
}) => {
  const dispatch = useDispatch();
  const { individualBranch } = useSelector((state) => state.branch);
  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      reset();
      return;
    }
    if (id) {
      dispatch(GetIndividualBranch(id));
    }
  }, [id]);

  // Create a mapping of payer IDs to provider code fields
  const payerFieldMapping = useMemo(() => {
    return {
      1: "provider_code_bupa",
      2: "provider_code_tawuniya",
      3: "provider_code_medgulf",
      4: "provider_code_malath",
      5: "provider_code_saico",
      7: "provider_code_sabb",
      8: "provider_code_gulf_union",
      9: "provider_code_alrajhi",
      11: "provider_code_gig",
      12: "provider_code_wataniyarn",
      13: "provider_code_medivisarn",
      14: "provider_code_tcs",
      15: "provider_code_globemed",
      16: "provider_code_nextcare",
      17: "provider_code_al_etihad",
      18: "provider_code_mednet",
    };
  }, []);

  useEffect(() => {
    if (!id || !individualBranch?.length) return;

    const data = individualBranch?.[0];
    const providerCodes = data?.provider_code_details || [];

    // Set basic branch info
    setValue("branch_name_eng", data?.branch_name || "");
    setValue("branch_name_arabic", data?.branch_arabic_name || "");
    setValue("mobile_no", data?.branch_telephone || "");

    setValue(
      "branch_type",
      branchList?.find((item) => item?.title === data?.branch_type)
    );

    setValue("cchi_no", data?.branch_cchino || "");
    setValue("nphies_id", data?.branch_nphies_code || "");
    setValue("vat_no", data?.branch_vatno || "");
    setValue("cr_no", data?.branch_crno || "");
    setValue("building_no", data?.branch_building_no || "");
    setValue("additional_no", data?.branch_additional_no || "");
    setValue("unit_no", data?.branch_unit_no || "");
    setValue("street_name", data?.branch_street_name || "");
    setValue("district", data?.branch_district || "");
    setValue("zip_code", data?.branch_zip_code || "");
    setValue(
      "city",
      cityList?.find((item) => item?.value == data?.branch_city) || ""
    );

    // Set country
    if (data?.branch_country) {
      const country = nationalityData.find(
        (c) => c.value === data.branch_country
      );
      if (country) {
        setValue("country", {
          value: country.value,
          title: country.title,
        });
      }
    }

    // Set provider codes
    providerCodes.forEach((pc) => {
      const fieldName = payerFieldMapping[pc.payer_id];
      if (fieldName) {
        setValue(fieldName, pc.provider_code || "");
      }
    });
  }, [individualBranch, setValue, id, payerFieldMapping]);

  return (
    <Row>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="branch_name_eng"
          label="Branch Name (English)"
          placeholder="Branch Name (English)"
          control={control}
          errors={errors}
          disabled={view}
          required={true}
          size="sm"
          rules={{
            validate: (v) => isEnglish(v) || "Must be English letters only",
          }}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="branch_name_arabic"
          label="Branch Name (Arabic)"
          placeholder="Branch Name (Arabic)"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
          rules={{
            validate: (v) => !v || isArabic(v) || "Must be Arabic letters only",
          }}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="mobile_no"
          label="Mobile No."
          control={control}
          placeholder="05xxxxxxxx"
          disabled={view}
          size="sm"
          errors={errors}
          rules={{
            validate: (v) =>
              !v ||
              /^05\d{8}$/.test(v) ||
              "Mobile number must start with 05 and be 10 digits",
          }}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label">
          Branch Type <span className="text-danger">*</span>
        </label>
        <Controller
          name="branch_type"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              disabled={view}
              isMulti={false}
              options={branchList?.map((item) => ({
                value: item?.value,
                label: item?.title,
              }))}
              isClearable={true}
              value={
                field.value
                  ? {
                      value: field.value.value,
                      label: field.value.title,
                    }
                  : null
              }
              onChange={(selected) => {
                setValue(
                  "branch_type",
                  selected
                    ? {
                        value: selected.value,
                        title: selected.label,
                      }
                    : null
                );
                clearErrors("branch_type");
              }}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="cchi_no"
          label="CCHI No"
          placeholder="CCHI No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
          required={true}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="nphies_id"
          label="Nphies ID"
          placeholder="Nphies ID"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
          required={true}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="vat_no"
          label="Vat No"
          placeholder="Vat No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="cr_no"
          label="CR No"
          placeholder="CR No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="building_no"
          label="Building No"
          placeholder="Building No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="additional_no"
          label="Additional No"
          placeholder="Additional No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="unit_no"
          label="Unit No"
          placeholder="Unit No"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="street_name"
          label="Street Name"
          placeholder="Street Name"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="district"
          label="District"
          placeholder="District"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="zip_code"
          label="Zip Code"
          placeholder="Zip Code"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label">City</label>
        <Controller
          name="city"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              disabled={view}
              isMulti={false}
              options={cityList?.map((item) => ({
                value: item?.value,
                label: item?.title,
              }))}
              isClearable={true}
              value={
                field.value
                  ? {
                      value: field.value.value,
                      label: field.value.title,
                    }
                  : null
              }
              className={classNames({
                "is-invalid": errors.city || (isSubmitted && !watch("city")),
              })}
              onChange={(selected) => {
                setValue(
                  "city",
                  selected
                    ? {
                        value: selected.value,
                        title: selected.label,
                      }
                    : null
                );
                clearErrors("city");
              }}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label mb-2">Country</label>
        <Controller
          name="country"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              disabled={view}
              isMulti={false}
              options={nationalityData?.map((item) => ({
                value: item.value,
                label: item.title,
              }))}
              isClearable={true}
              value={
                field.value
                  ? {
                      value: field.value.value,
                      label: field.value.title,
                    }
                  : null
              }
              onChange={(selected) => {
                setValue(
                  "country",
                  selected
                    ? {
                        value: selected.value,
                        title: selected.label,
                      }
                    : null
                );
                clearErrors("country");
              }}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              getOptionValue={(option) => option.value}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>
      <Col xs={12} sm={6} md={4}></Col>
      <Col xs={12}>
        <FalconComponentCard className="mt-3 border shadow-none">
          <FalconComponentCard.Header className="border-bottom text-info fs-10 fw-medium badge-subtle-info">
            Provider Code
          </FalconComponentCard.Header>
          <FalconComponentCard.Body>
            <Row>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_bupa"
                  label="Provider Code Bupa"
                  placeholder="Provider Code Bupa"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_tawuniya"
                  label="Provider Code Tawuniya"
                  placeholder="Provider Code Tawuniya"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_medgulf"
                  label="Provider Code MedGulf"
                  placeholder="Provider Code MedGulf"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_malath"
                  label="Provider Code Malath"
                  placeholder="Provider Code Malath"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_saico"
                  label="Provider Code Saico"
                  placeholder="Provider Code Saico"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_sabb"
                  label="Provider Code Sabb"
                  placeholder="Provider Code Sabb"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_gulf_union"
                  label="Provider Code Gulf Union"
                  placeholder="Provider Code Gulf Union"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_alrajhi"
                  label="Provider Code Alrajhi Takaful"
                  placeholder="Provider Code Alrajhi Takaful"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_gig"
                  label="Provider Code GIG"
                  placeholder="Provider Code GIG"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_wataniyarn"
                  label="Provider Code Wataniyarn"
                  placeholder="Provider Code Wataniyarn"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_medivisarn"
                  label="Provider Code Medivisarn"
                  placeholder="Provider Code Medivisarn"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_tcs"
                  label="Provider Code TCS"
                  placeholder="Provider Code TCS"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_globemed"
                  label="Provider Code Globemed"
                  placeholder="Provider Code Globemed"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_nextcare"
                  label="Provider Code Nextcare"
                  placeholder="Provider Code Nextcare"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_al_etihad"
                  label="Provider Code Al-Etihad"
                  placeholder="Provider Code Al-Etihad"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="provider_code_mednet"
                  label="Provider Code MedNet"
                  placeholder="Provider Code MedNet"
                  control={control}
                  errors={errors}
                  disabled={view}
                  size="sm"
                />
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </FalconComponentCard>
      </Col>
    </Row>
  );
};

export default AddBranchForm;
